/**
 * *Remove* for a selection (`equipment-page-table`, R14–R16): the confirmation, the gate that keeps
 * a placed row, and the removal itself.
 *
 * The confirmation is the design system's `Modal` in its coral, destructive look. It lists what
 * goes, and — where any selected row is on the plan — what stays and where it is
 * (*Treadmill-9 · Ground floor › Cardio*). A placed row is never sent: the server refuses one on a
 * saved plan too (a 409), but the client gate is what keeps the request from being made.
 *
 * A render prop, as `EquipmentIdentify` is: the page draws the bar where the stage puts it, and this
 * hands it the press. It must sit inside the page's `ToastProvider`.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Modal } from '@repo/design-system';
import { space, typeRole } from '@repo/design-tokens';
import { placementWhereFor, registerRowName } from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';
import type { TwinAssetResponse } from '@repo/front-end-services';

import { useEquipmentRowWrites, type EquipmentWriteDeps } from './useEquipmentRowWrites';

export interface EquipmentRemoveSelectedProps {
  /** The selection, in the order it was ticked. */
  selectedIds: readonly number[];
  rows: readonly TwinAssetResponse[];
  /** The register-joined plan, for where a placed row is (R15). */
  layout: FloorPlannerLayout | null | undefined;
  /** Whether a row is on the plan — the step's one placement gate (R14). */
  isPlaced: (assetId: number) => boolean;
  /**
   * Whether the plan is known, so the gate can say where every row is (R14). Asked again while the
   * dialog is open: a plan that becomes unknown would otherwise empty the *stays* list.
   */
  removeAvailable: boolean;
  writes: EquipmentWriteDeps;
  /** After a removal lands: clear the selection (R12). */
  onRemoved: () => void;
  children: (openRemove: () => void) => React.ReactNode;
}

/** How many names the confirmation lists before *and {m} more* (R14). */
const LISTED = 10;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  font-size: ${typeRole.body1.fontSize};
  line-height: ${typeRole.body1.lineHeight};

  p,
  ul {
    margin: 0;
  }
  ul {
    padding-left: ${space[4]};
  }
`;

const assetWord = (n: number): string => (n === 1 ? 'asset' : 'assets');

/** *{floor} › {space}*, *{floor}*, or *on the plan* where the place cannot be resolved (R14, R15). */
export const whereText = (
  layout: FloorPlannerLayout | null | undefined,
  assetId: number,
): string => {
  const where = placementWhereFor(layout, assetId);
  if (!where || !where.floorName) return 'on the plan';
  return where.spaceName ? `${where.floorName} › ${where.spaceName}` : where.floorName;
};

export const EquipmentRemoveSelected: React.FC<EquipmentRemoveSelectedProps> = ({
  selectedIds,
  rows,
  layout,
  isPlaced,
  removeAvailable,
  writes,
  onRemoved,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { remove } = useEquipmentRowWrites(writes);
  // After a removal, focus moves to the main panel's title (A2) — once the dialog has closed: its
  // own clean-up hands focus back to the bar's Remove, which the cleared selection has just hidden.
  // A mount effect here runs after the dialog's clean-up in the same commit, so it lands last.
  const focusTitle = useRef(false);
  // Where focus was when *Remove* was pressed. The dialog records its trigger only after *Cancel*
  // has taken focus (its `autoFocus`), so on Escape it would restore to that closed button and drop
  // focus at the body; this is its declared fallback instead.
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open || !focusTitle.current) return;
    focusTitle.current = false;
    document.getElementById('equipment-main-title')?.focus();
  }, [open]);

  // Worked out only while the dialog is open: the host is always mounted, and the page re-renders
  // on every keystroke and re-read.
  const { removable, staying } = useMemo(() => {
    if (!open) return { removable: [], staying: [] };
    const byId = new Map(rows.map(row => [row.id, row]));
    const chosen = selectedIds
      .map(id => byId.get(id))
      .filter((row): row is TwinAssetResponse => Boolean(row));
    // The gate: a row on the plan is never sent, and nothing is while the plan is unknown.
    return {
      removable: removeAvailable ? chosen.filter(row => !isPlaced(row.id)) : [],
      staying: chosen.filter(row => isPlaced(row.id)),
    };
  }, [open, rows, selectedIds, isPlaced, removeAvailable]);
  const k = removable.length;
  const j = staying.length;

  const close = () => {
    if (!busy) setOpen(false);
  };

  const confirm = async () => {
    if (!removeAvailable || k === 0) return;
    setBusy(true);
    const outcome = await remove(removable.map(row => row.id));
    setBusy(false);
    if (outcome === 'removed') {
      focusTitle.current = true;
      onRemoved();
    }
    setOpen(false);
  };

  return (
    <>
      {children(() => {
        opener.current = document.activeElement as HTMLElement | null;
        setOpen(true);
      })}
      <Modal
        open={open}
        onClose={close}
        returnFocusTo={opener}
        dismissible={!busy}
        tone="coral"
        width="sm"
        title={k > 0 ? `Remove ${k} ${assetWord(k)} from the register?` : 'Nothing can be removed'}
        actions={
          <>
            <Button variant="frosted" autoFocus onClick={close} aria-disabled={busy || undefined}>
              Cancel
            </Button>
            {k > 0 && (
              <Button variant="destructive" loading={busy} onClick={() => void confirm()}>
                Remove {k}
              </Button>
            )}
          </>
        }
      >
        <Body data-testid="equipment-remove-body">
          {k > 0 ? (
            <>
              <ul aria-label="Removed">
                {removable.slice(0, LISTED).map(row => (
                  <li key={row.id}>{registerRowName(row)}</li>
                ))}
              </ul>
              {k > LISTED && <p>and {k - LISTED} more</p>}
              <p>This cannot be undone.</p>
            </>
          ) : !removeAvailable ? (
            <p>Nothing can be removed until the plan has loaded.</p>
          ) : (
            <p>
              Nothing can be removed: all {j} {j === 1 ? 'is' : 'are'} on the plan.
            </p>
          )}
          {j > 0 && (
            <>
              <p>
                {j} {j === 1 ? 'stays because it is' : 'stay because they are'} on the plan
              </p>
              <ul aria-label="Staying">
                {staying.map(row => (
                  <li key={row.id}>
                    {registerRowName(row)} · {whereText(layout, row.id)}
                  </li>
                ))}
              </ul>
              <p>Take them off the plan in Floor Plan first.</p>
            </>
          )}
        </Body>
      </Modal>
    </>
  );
};

export default EquipmentRemoveSelected;
